import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CustomValidators } from '@forms/custom-validators';
import { Period } from '@models/period';
import { Paginator, PaginatorItems, Table } from '@models/table';
import { DealersService } from '@services/dealers.service';
import { ReportsService } from '@services/reports.service';
import { firstValueFrom } from 'rxjs';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { AppService } from '@services/app.service';
import { saveAs } from "file-saver-es";
import { PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_EXPORT_EXCEL, PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_EXPORT_PDF } from 'src/app/const/permitions';
import { set } from 'date-fns';
import { formatNumberToLocal } from 'src/app/utils/format-number-to-local';

@Component({
  selector: 'app-portfolio-management-details-report-modal',
  templateUrl: './portfolio-management-details-report-modal.component.html',
  styleUrl: './portfolio-management-details-report-modal.component.scss'
})
export class PortfolioManagementDetailsReportModalComponent extends ModalComponent<PortfolioManagementDetailsReportModalComponent> implements OnInit{
  private readonly _permissions: string[]
  private hasDoneFirstSearch: boolean = false;

  public table: Table<any> = this.createTable();
  public readonly hasAccessToAnyDealer: boolean;

  public readonly activeName: string;
  public loading: boolean = false;

  private readonly _identifierCode: string | null;
  private readonly _selledAt: Period | null;
  private readonly _active: number;

  public isExportingExcel: boolean = false;
  public isExportingPdf: boolean = false;
  private timeouts: any[] = [];



  public constructor(private readonly _datePipe: DatePipe,
                     private readonly _reportsService: ReportsService,
                     @Inject(AppService) appService: AppService,
                     @Inject(MAT_DIALOG_DATA) public readonly matDialogData,
  ) {
    super();

    this._identifierCode = matDialogData.identifierCode;
    this._selledAt = matDialogData.selledAt;
    this._active = matDialogData.active;
    this.activeName = matDialogData.activeName;

    this._permissions = appService.getPermissions();

    this.hasAccessToAnyDealer = appService.getAllAllowedDealers().length > 0;
  }

  public ngOnInit() {
    this.dialogRef.afterClosed().subscribe(() => {
      this.clearAllTimeouts();
    });
    this.filter();
  }



  private clearAllTimeouts(): void {
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeouts = [];
  }

  public filter(paginator?: Paginator): void {
    if (!this.hasAccessToAnyDealer) {
      return;
    }

    this.loading = true;

    if (!paginator) {
      paginator = this.table.paginator;
    }

    firstValueFrom(
      this._reportsService.getPortfolioDetailsReport(this._identifierCode, this._selledAt, this._active, paginator, this.hasDoneFirstSearch)
    )
      .then((resp: PaginatorItems<any>) => {
          this.table.data = resp.items;
          this.table.paginator.length = resp.totalCount;
          this.table.paginator.index = paginator.index;
          this.table.paginator.pageSize = paginator.pageSize;
      })
      .finally(() => {
        this.hasDoneFirstSearch = true;

        const timeoutId = setTimeout(() => {
          this.loading = false;
        });
        this.timeouts.push(timeoutId);
      });
  }

  public pageChange(paginator: Paginator): void {
    this.loading = true;
    this.filter(paginator);
  }

  public get hasPermissionToExportExcel(): boolean {
    return this._permissions.some(p => [PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_EXPORT_EXCEL].includes(p));
  }

  public get hasPermissionToExportPdf(): boolean {
    return this._permissions.some(p => [PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_EXPORT_PDF].includes(p));
  }

  public async exportPdfReport(): Promise<void> {
    try {

      this.isExportingPdf = true;
      const result = await firstValueFrom(this._reportsService.exportPortfolioDetailsToPdfReport(this._identifierCode, this._selledAt, this._active));

      this.download(result, "relatorio-detalhes-carteira.pdf", "application/pdf");
    } finally {
      this.isExportingPdf = false;
    }
  }

  public async exportWorksheetReport(): Promise<void> {
    try {
      this.isExportingExcel = true;
      const result = await firstValueFrom(this._reportsService.exportPortfolioDetailsToXlsxReport(this._identifierCode, this._selledAt, this._active));

      this.download(result.fileBase64, result.fileName, result.contentType);
      this.isExportingExcel = false;
    } finally {
      this.isExportingExcel = false;
    }
  }

  public download(
    data: string | Blob,
    name: string,
    contentType: string
  ): void {
    let blob: Blob;

    if (!(data instanceof Blob)) {
      blob = this.base64toBlob(data, contentType);
    } else {
      blob = data;
    }

    var file = new File([blob], name, { type: "application/octet-stream" });
    saveAs(file, name);
  }

  private base64toBlob(base64Data: string, contentType: string) {
    const sliceSize = 512;
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  private createTable(): Table<any> {
    return {
      headers: [
        {
          key: "contract",
          value: "Contrato",
        },
        {
          key: "group",
          value: "Grupo",
        },
        {
          key: "quota",
          value: "Cota",
        },
        {
          key: "version",
          value: "Versão",
        },
        {
          key: "saleDate",
          value: "Venda",
        },
        {
          key: "dueDate",
          value: "Vencimento",
        },
        {
          key: "sellerDocument",
          value: "CPF vendedor",
        },
        {
          key: "cancellationDate",
          value: "Cancelamento",
        },
        {
          key: "asset",
          value: "Bem",
        },
        {
          key: "assetValue",
          value: "Valor Bem",
        },
        {
          key: "contemplationDate",
          value: "Contemplação",
        },
        {
          key: "contemplationType",
          value: "Tipo Contemplação",
        },
        {
          key: "deliveredAt",
          value: "Entrega",
        },
        {
          key: "dealer",
          value: "Fornecedor",
        },
        {
          key: "paidPercentage",
          value: "% Pago",
        },
        {
          key: "paidValue",
          value: "Valor Pago",
        },
        {
          key: "toBePaidPercentage",
          value: "% a Pagar",
        },
        {
          key: "toBePaidValue",
          value: "Valor a Pagar",
        },
        {
          key: "paidInstallments",
          value: "Parcelas Pagas",
        },
        {
          key: "installmentsToBePaid",
          value: "Parcelas a Pagar",
        },
        {
          key: "arrears",
          value: "Parcelas Atrasadas",
        },
        {
          key: "groupClosureDate",
          value: "Enc. Grupo",
        },
        {
          key: "transferredQuota",
          value: "Cota Transf.",
        },
        {
          key: "clientName",
          value: "Nome cliente",
        },
        {
          key: "ddd",
          value: "DDD",
        },
        {
          key: "phone",
          value: "Telefone",
        },
        {
          key: "email",
          value: "E-mail",
        },
        {
          key: "situation",
          value: "Situação",
        }
      ],
      data: [],
      style: {
        table: {
          height: "500px",
        },
        data: [
          {
            key: "assetValue",
            transform: (value) => value && formatNumberToLocal(value),
          },
          {
            key: "paidValue",
            transform: (value) => value && formatNumberToLocal(value),
          },
          {
            key: "toBePaidValue",
            transform: (value) => value && formatNumberToLocal(value),
          },
          {
            key: "saleDate",
            transform: (value) =>
              this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "dueDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "cancellationDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "contemplationDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "deliveredAt",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "groupClosureDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
        ]
      },
      checkbox: false,
      actions: false,
      paginator: {
        index: 1,
        itensPerPage: [10, 15, 20, 50, 100],
        pageSize: 10
      },
      messageWithoutDates: "Não há dados para exibir no momento.",
    };
  }
}
